import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import mobileAppBridge from 'app/libs/MobileAppBridge';
import { calcInterest } from 'app/libs/loan';
import { Base64 } from 'js-base64';
import qs from 'query-string';
import useApplyEncarpay from 'app/hook/encarpay/useApplyEncarpay';
import useSsrCookie from 'app/hook/common/useSsrCookie';

export default function useEncarpayLimitCheck() {
	const { setCookie, deleteCookie, getCookie } = useSsrCookie();
	const { search } = useLocation();
	const { 'WT.hit': dsHit, encarpay: hasEncarpayLimitCheck } = qs.parse(search);

	/**
	 * loanhome_encarpay : 중고차론 홈에서 진입 / 한도조회 기록 존재 / 최근 본 차량 존재일때
	 * alimtalk_reqular_application : M_엔카페이_고객_일반 신규 케이스 알림톡에서 진입
	 */
	const getEncarpayLimitEnlogHit = useCallback(() => {
		let enlogHit = '';
		if (dsHit === 'loanhome_encarpay' && hasEncarpayLimitCheck === 'N') enlogHit = 'loanhome_encarpay_apply';
		if (dsHit === 'loanhome_encarpay' && hasEncarpayLimitCheck === 'Y') enlogHit = 'loanhome_apply.retry';
		if (dsHit === 'loanhome_apply.recentcar_y') enlogHit = 'loanhome_apply.recentcar_y';
		if (dsHit === 'loanhome_apply.recentcar_n') enlogHit = 'loanhome_apply.recentcar_n';
		if (dsHit === 'detail_encarpay' && hasEncarpayLimitCheck === 'N') enlogHit = 'detail_encarpay_loanlimit';
		if (dsHit === 'detail_encarpay' && hasEncarpayLimitCheck === 'Y') enlogHit = 'detail_encarpay_loanlimit_retry';
		if (dsHit === 'alimtalk_encarpay' && hasEncarpayLimitCheck === 'N') enlogHit = 'alimtalk_encarpay';
		if (dsHit === 'alimtalk_encarpay' && hasEncarpayLimitCheck === 'Y') enlogHit = 'alimtalk_encarpay_retry';
		if (dsHit === 'alimtalk_reqular_application' && hasEncarpayLimitCheck === 'Y') enlogHit = 'alimtalk_reqular_application';
		if (dsHit === 'detail_diag_encarpay_loanlimit') enlogHit = 'detail_diag_encarpay_loanlimit';
		if (dsHit === 'detail_normal_encarpay_loanlimit') enlogHit = 'detail_normal_encarpay_loanlimit';
		if (dsHit === 'detail_mg_encarpay_loanlimit') enlogHit = 'detail_mg_encarpay_loanlimit';

		return enlogHit;
	}, [dsHit, hasEncarpayLimitCheck]);

	const getEncarpayLimitCheckHash = useCallback(() => getCookie('ENCARPAY_APPLICATION_HASH'), [getCookie]);

	const setEncarpayLimitCheckHash = useCallback(
		({ hash, encarpayLimitExpirationPeriod }) => {
			setCookie('ENCARPAY_APPLICATION_HASH', hash, 0, 0, 0, encarpayLimitExpirationPeriod);
		},
		[setCookie],
	);

	const deleteEncarpayLimitCheckHash = useCallback(() => deleteCookie('ENCARPAY_APPLICATION_HASH'), [deleteCookie]);

	const setEncarpayLimitCheckAppData = useCallback(({ carId, clientName, carMaximumLimit, creditMaximumLimit, expiredAt, interestRate }) => {
		mobileAppBridge().getEncarpayLimitCheck({
			carId,
			clientName: Base64.encode(clientName),
			carMaximumLimit,
			creditMaximumLimit,
			expiredAt,
			interestRate,
		});
	}, []);

	const { getEncarpayApplyState, mainInterestRate, interestInfo } = useApplyEncarpay();

	const interestRateSortByRateAscending = useMemo(() => [...interestInfo].sort((a, b) => a.interestRate - b.interestRate), [interestInfo]);

	const financialCompanyCount = useMemo(() => interestRateSortByRateAscending.length, [interestRateSortByRateAscending.length]);

	const { minRate, maxRate } = useMemo(() => {
		const items = [interestRateSortByRateAscending[0]?.interestRate, interestRateSortByRateAscending[1]?.interestRate];

		return { minRate: Math.min(...items), maxRate: Math.max(...items) };
	}, [interestRateSortByRateAscending]);

	const { applicationId, carMaximumLimit, applicationStorage } = useMemo(() => getEncarpayApplyState(), [getEncarpayApplyState]);
	const applicationInfo = useMemo(() => applicationStorage[applicationId], [applicationId, applicationStorage]);

	const calcInterestOnLoans = useCallback(
		(prepayAmount = 0, months, interestRate) => calcInterest(prepayAmount, months, interestRate, carMaximumLimit),
		[carMaximumLimit],
	);

	const defaultPrepay = useMemo(
		() =>
			interestRateSortByRateAscending.reduce(
				(acc, cur) => ({
					...acc,
					[cur.financialCompany]: { prepay: 0, monthly: 60, interestRate: cur.interestRate },
				}),
				{},
			),
		[interestRateSortByRateAscending],
	);

	return {
		defaultPrepay,
		interestRateSortByRateAscending,
		financialCompanyCount,
		mainInterestRate,
		minRate,
		maxRate,
		carMaximumLimit,
		applicationInfo,
		calcInterestOnLoans,
		getEncarpayLimitEnlogHit,
		getEncarpayLimitCheckHash,
		setEncarpayLimitCheckHash,
		deleteEncarpayLimitCheckHash,
		setEncarpayLimitCheckAppData,
	};
}
